/**
 * NX-4971 — advisory payslip-verifier detail modal.
 *
 * Opens from the verifier badge/action. Fetches the verdict detail (which includes
 * the displayable `input`: de-identified payslip S3 URL + FPS/RPN sent to the model)
 * and shows verdict, findings, telemetry, and the verified input.
 * ADVISORY ONLY — nothing here blocks confirming the draft payroll.
 */
import AppModalContainer from '@nxsys/components/AppModalContainer';
import { IRELAND } from '@nxsys/store/constants/keys';
import {
  useGetPayslipVerification,
  useSetPayslipGroundTruth,
} from '@nxsys/hooks/api/payroll';
import {
  DraftPayroll,
  PayslipVerdict,
  PayslipVerificationRef,
} from '@nxsys/types/models/payroll';
import { Button, Empty, Form, Input, Select, Typography, message } from 'antd';
import { useEffect, useState } from 'react';
import { PayslipVerdictBadge } from './PayslipVerdictBadge';

// Ground-truth verdicts a reviewer can record (NEEDS_REVIEW is not a ground truth).
const GROUND_TRUTH_OPTIONS: { value: PayslipVerdict; label: string }[] = [
  { value: 'PASS', label: 'PASS' },
  { value: 'PASS_WITH_WARNINGS', label: 'WARNING' },
  { value: 'ERRORS_FOUND', label: 'ERROR' },
];

type Props = {
  isOpen: boolean;
  toggle: () => void;
  record?: DraftPayroll | null;
  onRerun?: (record: DraftPayroll) => void;
  rerunLoading?: boolean;
  /** Fired after a review is saved so the parent list can refetch its verdict badges. */
  onSaved?: () => void;
};

const preStyle: React.CSSProperties = {
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  maxHeight: 260,
  overflow: 'auto',
  background: '#fafafa',
  border: '1px solid #eee',
  padding: 8,
  fontSize: 12,
  marginBottom: 0,
};

// NX-4971 — plain-language summary of what Penny recomputes/checks (advisory only).
// UK (PAYE / Umbrella): tax code + bands, NI, employer costs.
const RULES_UK = [
  'Earnings — the pay lines (salary, holiday pay, bonus) add up to gross.',
  'Tax — recomputed from the tax code and bands; must match the payslip.',
  'National Insurance — employee & employer amounts match the NI category, thresholds and rates.',
  'Pension — contributions match the scheme method & rate; an active member is not £0.',
  'Loans & attachment orders — student / postgraduate loans and AEOs deducted correctly.',
  'Net pay — gross minus all deductions; equals the FPS net (PayAfterStatDedns).',
  'Year-to-date — previous YTD + this period = the new YTD, on every line.',
  'Employer costs & holiday-pay pot — employer NI, Apprenticeship Levy, holiday provision & pension are right; the pot carries forward (carried = brought-forward + accrued − taken); no negatives.',
  'Tolerance — small rounding differences are ignored (tax ±£0.10, everything else ±£0.02); only larger gaps are flagged.',
];

// Ireland (PAYE / Umbrella): credits + cut-off, USC, PRSI, LPT — very different from UK.
const RULES_IE = [
  'Earnings — the pay lines add up; holiday pay accrues at the set holiday-pay rate.',
  'Gross bases — the four printed bases (tax, USC, employee-PRSI, employer-PRSI) reconcile.',
  'Income tax — tax credits + standard-rate cut-off, charged at 20% then 40%; must match the payslip.',
  'USC — charged across its bands / cut-offs on the USC base.',
  'PRSI — employee & employer amounts match the PRSI class thresholds and rates.',
  'LPT & pension — Local Property Tax and pension (auto-enrolment / private PRSA) deducted correctly.',
  'Net pay — gross minus all deductions.',
  'Year-to-date — previous YTD + this period = the new YTD; on the cumulative basis the tax credit / cut-off / USC bands are pro-rated by the weeks elapsed so far (the tax period).',
  'Tolerance — small rounding differences are ignored (tax & USC ±€0.10, everything else ±€0.02); only larger gaps are flagged.',
];

const PayslipVerdictModal: React.FC<Props> = ({
  isOpen,
  toggle,
  record,
  onRerun,
  rerunLoading,
  onSaved,
}) => {
  const { run: fetchDetail } = useGetPayslipVerification();
  const { run: saveGroundTruth, loading: savingGroundTruth } =
    useSetPayslipGroundTruth();
  const [detail, setDetail] = useState<PayslipVerificationRef | null>(null);

  // antd Form drives the review fields (asterisk + validation for free).
  const [form] = Form.useForm<{ verdict?: PayslipVerdict; message?: string }>();

  // Fetch the exact verification record the list badge points at, by its id — so the
  // modal always matches the row (no "latest by payrollId" divergence).
  const verificationId = record?.payslipVerificationRef?.id;

  // Fetch (or re-fetch) the record and seed the form. NOT memoized on `fetchDetail`
  // — `useAsync`'s `run` changes identity every render, so depending on it in the
  // effect below would cause an infinite fetch loop.
  const loadDetail = () => {
    if (!verificationId) return Promise.resolve(null);
    return fetchDetail({ id: verificationId }).then((res) => {
      const data = (res?.data as PayslipVerificationRef) || null;
      setDetail(data);
      // Seed the form with any previously-recorded review.
      form.setFieldsValue({
        verdict: data?.groundTruthVerdict ?? undefined,
        message: data?.groundTruthMessage ?? undefined,
      });
      return data;
    });
  };

  useEffect(() => {
    if (!isOpen || !verificationId) {
      setDetail(null);
      form.resetFields();
      return;
    }
    loadDetail();
    // Intentionally only re-run when the modal opens or the target id changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, verificationId]);

  // Prefer the freshly-fetched detail (carries `input`); fall back to the row ref.
  const v = detail || record?.payslipVerificationRef;
  const input = v?.input;
  // Ireland and UK are audited by completely different rules — show the right set.
  const rules = input?.payrollCountry === IRELAND ? RULES_IE : RULES_UK;

  // onFinish only fires after Form validates (verdict required) — no manual checks.
  const handleSaveGroundTruth = (values: {
    verdict?: PayslipVerdict;
    message?: string;
  }) => {
    if (!verificationId || !values.verdict) return;
    saveGroundTruth({
      id: verificationId,
      verdict: values.verdict,
      message: values.message?.trim() || undefined,
    }).then((res) => {
      if (res?.success) {
        message.success('Review saved.');
        loadDetail();
        // Refresh the parent payroll list so its verdict badge reflects the review.
        onSaved?.();
      }
    });
  };

  return (
    <AppModalContainer
      isOpen={isOpen}
      toggle={toggle}
      title="Penny — Payslip Verifier (advisory)"
      width="80vw"
      actions={[
        ...(onRerun && record
          ? [
              {
                name: 'Re-run',
                variant: 'outlined' as const,
                loading: rerunLoading,
                onClick: () => onRerun(record),
              },
            ]
          : []),
        {
          name: 'Close',
          buttonType: 'primary' as const,
          onClick: toggle,
        },
      ]}
    >
      {!v ? (
        <Empty description="This draft payslip has not been verified yet." />
      ) : (
        <div className="flex flex-col gap-4">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 16,
              alignItems: 'start',
            }}
          >
          {/* ── 1. INPUT — what was sent to the model ── */}
          <div>
            <Typography.Title level={4} style={{ marginBottom: 8 }}>
              Input
            </Typography.Title>
            {!input ? (
              <Typography.Text type="secondary">
                Input details unavailable.
              </Typography.Text>
            ) : (
              <div className="flex flex-col gap-3">
                <div>
                  <Typography.Text strong>
                    Current reconstructed de-identified payslip (PDF)
                  </Typography.Text>
                  <div>
                    {input.payslipS3Url ? (
                      <Typography.Link
                        href={input.payslipS3Url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open de-identified payslip PDF ↗
                      </Typography.Link>
                    ) : (
                      <Typography.Text type="secondary">
                        PDF not available (S3 URL unavailable).
                      </Typography.Text>
                    )}
                  </div>
                </div>

                {input.previousPayslipS3Url ? (
                  <div>
                    <Typography.Text strong>
                      Previous reconstructed de-identified payslip (PDF)
                    </Typography.Text>
                    <div>
                      <Typography.Link
                        href={input.previousPayslipS3Url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open previous de-identified payslip PDF ↗
                      </Typography.Link>
                    </div>
                  </div>
                ) : null}

                {input.fps ? (
                  <div>
                    <Typography.Text strong>
                      Current reconstructed de-identified FPS (XML)
                    </Typography.Text>
                    <pre style={preStyle}>{input.fps}</pre>
                  </div>
                ) : null}

                {input.previousFps ? (
                  <div>
                    <Typography.Text strong>
                      Previous reconstructed de-identified FPS (XML)
                    </Typography.Text>
                    <pre style={preStyle}>{input.previousFps}</pre>
                  </div>
                ) : null}

                {input.previousHpPot ? (
                  <div>
                    <Typography.Text strong>
                      Previous HP pot (brought forward)
                    </Typography.Text>
                    <pre style={preStyle}>{input.previousHpPot}</pre>
                  </div>
                ) : null}

                {input.p45GrossToDate ? (
                  <div>
                    <Typography.Text strong>P45 previous employment</Typography.Text>
                    <pre style={preStyle}>
                      {`p45GrossToDate: ${input.p45GrossToDate}    p45TaxToDate: ${input.p45TaxToDate ?? '0.00'}`}
                    </pre>
                  </div>
                ) : null}

                {input.rpn ? (
                  <div>
                    <Typography.Text strong>
                      Current reconstructed de-identified RPN (JSON)
                    </Typography.Text>
                    <pre style={preStyle}>{input.rpn}</pre>
                  </div>
                ) : null}

                {input.previousPsr ? (
                  <div>
                    <Typography.Text strong>
                      Previous reconstructed de-identified PSR (XML)
                    </Typography.Text>
                    <pre style={preStyle}>{input.previousPsr}</pre>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* ── 2. RULE — what Penny checks (advisory) ── */}
          <div>
            <Typography.Title level={4} style={{ marginBottom: 8 }}>
              Rules
            </Typography.Title>
            <ol
              style={{
                paddingLeft: 20,
                margin: 0,
                listStyleType: 'decimal',
                listStylePosition: 'outside',
              }}
            >
              {rules.map((rule) => (
                <li key={rule} style={{ marginBottom: 2, fontSize: 13 }}>
                  {rule}
                </li>
              ))}
            </ol>
          </div>

          {/* ── 3. OUTPUT — the verdict findings / error ── */}
          <div>
            <Typography.Title level={4} style={{ marginBottom: 8 }}>
              Output
            </Typography.Title>
            <Typography.Text strong>Verdict</Typography.Text>
            <div style={{ margin: '4px 0 12px' }}>
              <PayslipVerdictBadge verification={v} large />
            </div>
            <Typography.Text strong>Findings</Typography.Text>
            <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
              {v.message || '—'}
            </Typography.Paragraph>
            {v.errorMessage ? (
              <div style={{ marginTop: 8 }}>
                <Typography.Text strong type="danger">
                  Error
                </Typography.Text>
                <Typography.Paragraph
                  type="danger"
                  style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}
                >
                  {v.errorMessage}
                </Typography.Paragraph>
              </div>
            ) : null}

            {/* ── Reviewer's verdict (ground truth) — the reviewer's own verdict, stored
                 separately from Penny's. Advisory: never changes the AI verdict. ── */}
            <div
              style={{
                marginTop: 16,
                paddingTop: 12,
                borderTop: '1px solid #eee',
              }}
            >
              <Typography.Text strong>Your review</Typography.Text>
              <Typography.Paragraph
                type="secondary"
                style={{ fontSize: 12, margin: '2px 0 4px' }}
              >
                What is the correct verdict for this payslip?
              </Typography.Paragraph>
              {v.groundTruthAt ? (
                <Typography.Paragraph
                  type="secondary"
                  style={{ fontSize: 12, margin: '2px 0 8px' }}
                >
                  Last recorded {new Date(v.groundTruthAt).toLocaleString()}
                </Typography.Paragraph>
              ) : (
                <Typography.Paragraph
                  type="secondary"
                  style={{ fontSize: 12, margin: '2px 0 8px' }}
                >
                  Not reviewed yet.
                </Typography.Paragraph>
              )}
              <Form
                form={form}
                layout="vertical"
                requiredMark
                onFinish={handleSaveGroundTruth}
                disabled={!verificationId}
              >
                <Form.Item
                  name="verdict"
                  label="Verdict"
                  rules={[
                    { required: true, message: 'Please select a verdict.' },
                  ]}
                  style={{ marginBottom: 12 }}
                >
                  <Select
                    placeholder="Verdict"
                    options={GROUND_TRUTH_OPTIONS}
                  />
                </Form.Item>
                <Form.Item
                  name="message"
                  label="Message"
                  style={{ marginBottom: 12 }}
                >
                  <Input.TextArea
                    placeholder="Message"
                    autoSize={{ minRows: 2, maxRows: 6 }}
                  />
                </Form.Item>
                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={savingGroundTruth}
                    disabled={!verificationId}
                  >
                    Save review
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </div>
          </div>
        </div>
      )}
    </AppModalContainer>
  );
};

export default PayslipVerdictModal;
